import { useMemo } from "react";
import useCalendarStore from "../store/calendar-store";
import { dateToLocalTime } from "../utils/format-date-with-locale";
import useSWR from "swr";
import type { EventStatus } from "../types/EventStatus";
import { swrFetcher } from "@/lib/api";
import { withLocale } from "@/lib/widget-locale";

export interface TransformedEvent {
  id: string;
  eventType: string; // "REGULAR_CLASS", "WORKSHOP", "RETREAT", "SPECIAL_EVENT"
  startTime: Date;
  endTime: Date;
  status: EventStatus;
  room: string | null;
  class: {
    title: any;
    descriptions: any;
    image: string | null;
    name: string;
    description: string | null;
    color?: string;
    id: string;
  } | null;
  teacher: {
    id: string | null;
    user: { name: string | null; avatar: string | null };
  } | null;
  bookings: {
    total: number;
    confirmed: number;
  };
  capacity: number;
  duration: number;
  pricingCategories: any[] | null;
  studio: {
    id: string;
    name: string;
    googleMapsLink: string | null;
  } | null;
  externalLink: string | null;

  // Workshop-specific information
  workshop?: {
    id: string;
    title: string;
    description: string | null;
    price: number;
    slug: string;
    instructor: string | null;
    location: string | null;
    featuredImage: string | null;
    content: string | null;
  } | null;
}

export function useClientEvents() {
  const studioId = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return new URLSearchParams(window.location.search).get("studioId");
  }, []);

  const { currentDate, view } = useCalendarStore();

  // Determine date range based on view
  const { startDate, endDate } = useMemo(() => {
    // Start with the current date
    const currentDateObj = new Date(currentDate);

    // Initialize startDate and endDate
    let startDate, endDate;

    if (view === "day") {
      // DAY VIEW still needs the FULL WEEK
      startDate = dateToLocalTime(new Date(currentDateObj));
      const day = startDate.getDay();

      const daysToSubtract = day === 0 ? 6 : day - 1;
      startDate.setDate(startDate.getDate() - daysToSubtract);
      startDate.setHours(0, 0, 0, 0);

      endDate = dateToLocalTime(new Date(startDate));
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
    } else if (view === "week" || view === "agenda") {
      // WEEK VIEW: Find Monday of current week
      startDate = dateToLocalTime(new Date(currentDateObj));
      const day = startDate.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday

      // Adjust to previous Monday (or stay on Monday if already Monday)
      const daysToSubtract = day === 0 ? 6 : day - 1;
      startDate.setDate(startDate.getDate() - daysToSubtract);
      startDate.setHours(0, 0, 0, 0);

      // End date is Sunday (6 days after Monday)
      endDate = dateToLocalTime(new Date(startDate));
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 0, 0, 0);
    } else if (view === "month") {
      // MONTH VIEW: First day of the current month
      startDate = dateToLocalTime(new Date(currentDateObj));
      startDate.setDate(1); // Set to 1st day of month
      startDate.setHours(0, 0, 0, 0);

      // Last day of the current month
      endDate = dateToLocalTime(new Date(startDate));
      // Move to first day of next month, then subtract one day
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0);
      endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  }, [currentDate, view]);

  // Configure SWR with caching options
  const { data, isLoading, error, mutate } = useSWR(
    startDate && endDate && studioId
      ? withLocale(
          `/api/widget/public/class-sessions/${studioId}?from=${startDate.toISOString()}&to=${endDate.toISOString()}`,
        )
      : null,
    swrFetcher,
    {
      // Optimize SWR options for performance
      revalidateOnFocus: true,
      revalidateIfStale: true,
      dedupingInterval: 10000, // 10 seconds
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      suspense: false,
    }
  );

  return {
    data,
    isLoading: isLoading,
    error,
    mutate,
  };
}
